import type {
	OptimizationModel,
	PriceLimitRuleModel,
	PricingCampaignModel,
	PricingEngineType,
	ProductsByPCModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { PricingCampaignsScope } from '../model/types'

import {
	entries,
	filter,
	fromPairs,
	groupBy,
	map,
	negate,
	orderBy,
	pipe,
	prop,
	propEq,
	propOr,
	sum,
	uniqBy,
} from 'lodash/fp'

import { Badge } from '@/shared/components/ui/badge'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { OptimizationStatus } from '@/shared/generated'
import { getFiltersQueryString } from '@/shared/tools/location'

export function getWarnings(optimization?: OptimizationModel) {
	const warnings: string[] = []
	if (
		optimization?.status !== OptimizationStatus.New &&
		optimization?.status !== OptimizationStatus.Pending
	) {
		if (optimization?.status === OptimizationStatus.Locked) {
			warnings.push('optimization_is_locked')
		} else if (optimization?.status === OptimizationStatus.Finished) {
			warnings.push('optimization_should_be_recreated')
		} else {
			warnings.push('optimization_is_not_editable')
		}
	}

	return warnings
}
export function warningsCellRenderer(
	value: string,
	{
		warnings,
	}: {
		warnings: string[]
	},
	t: TFunction
) {
	if (Number(value)) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge variant={Number(value) > 0 ? 'warning' : 'success'}>
						{value}
					</Badge>
				</TooltipTrigger>
				<TooltipContent>
					<>{warnings.map((val) => t(val, { defaultValue: val })).join('; ')}</>
				</TooltipContent>
			</Tooltip>
		)
	}

	return <Badge variant='success'>0</Badge>
}

export const percentOf = (total: number, count: number): number =>
	((count || 0) / (total || 0)) * 100
export const getAssortmentShare = (total: number, count: number): number => {
	const percent = percentOf(total, count)
	if (!Number.isFinite(percent)) {
		return 0
	} else {
		return Number(percent)
	}
}

/**
 * Check if limits has duplicated rules by name & on_violation
 * @param list
 * @returns
 */
export const ruleHasDuplicates = (list: PriceLimitRuleModel[]) =>
	uniqBy(({ name, on_violation }) => `${name}:${on_violation}`, list)
		?.length !== list?.length

export const isGlobalPricingCampaign = (
	campaign?: Pick<
		PricingCampaignModel,
		'optimization_group_id' | 'global_pc_set_id'
	>
) =>
	campaign?.optimization_group_id === null &&
	Boolean(campaign?.global_pc_set_id)

export const globalPricingCampaignProductsLink = (
	campaign: Pick<PricingCampaignModel, 'id'>
) =>
	`/p/products${getFiltersQueryString([
		{
			name: 'pricing_campaign_id',
			operation: 'in',
			value: [campaign.id],
		},
	])}`
export const loclaPricingCampaignProductsLink = (
	campaign: Pick<PricingCampaignModel, 'id' | 'optimization_group_id'>,
	scope?: PricingCampaignsScope
) =>
	`/p/og/${scope?.optimization_group_id || campaign.optimization_group_id}/products${getFiltersQueryString(
		[
			{
				name: 'pricing_campaign_id',
				operation: 'in',
				value: [campaign.id],
			},
		]
	)}`
export const createPricingCampaignLinkForProducts = (
	campaign: Pick<
		PricingCampaignModel,
		'id' | 'optimization_group_id' | 'global_pc_set_id'
	>
) => {
	if (isGlobalPricingCampaign(campaign)) {
		return globalPricingCampaignProductsLink(campaign)
	} else {
		return loclaPricingCampaignProductsLink(campaign)
	}
}

export const getCountsByPc: (
	data?: ProductsByPCModel[]
) => Record<string, number> = pipe([
	groupBy(prop('pricing_campaign_id')),
	entries,
	map(([id, data]: [string, ProductsByPCModel[]]) => [
		id,
		sum(map(propOr(0, 'product_count'), data)),
	]),
	fromPairs,
])
export const getOgsByPc: (
	data?: ProductsByPCModel[]
) => Record<string, string[]> = pipe([
	groupBy(prop('pricing_campaign_id')),
	entries,
	map(([id, data]: [string, ProductsByPCModel[]]) => [
		id,
		filter(Boolean, map(prop('optimization_group_id'), data)),
	]),
	fromPairs,
])

export const getPricingCampaignEngine = (campaign: {
	engine?: string
	settings?: {
		pricing_tactics?: {
			engine?: string
		}
	}
}): PricingEngineType =>
	(campaign.engine ??
		campaign.settings?.pricing_tactics?.engine) as PricingEngineType

export const getLocaPricingCampaigns = filter<PricingCampaignModel>(
	negate(isGlobalPricingCampaign)
)
export const getGlobalPricingCampaigns = filter<PricingCampaignModel>(
	isGlobalPricingCampaign
)
export const getGlobalPricingCampaignsByPcSet = (
	id: string,
	campaigns: PricingCampaignModel[]
) => filter(propEq('global_pc_set_id', id))(campaigns)

export const orderCampaigns = orderBy(['order'], ['asc'])

export const getPreOrderedCampaigns = (campaigns: PricingCampaignModel[]) =>
	[
		...orderCampaigns(getGlobalPricingCampaigns(campaigns)),
		...orderCampaigns(getLocaPricingCampaigns(campaigns)),
	].map((pc, index) => ({
		...pc,
		order: index + 1,
	})) as PricingCampaignModel[]
